import { manualUploadPageIds } from "./upload-queue";
import { isUploadReady } from "./page-persist";
import type { Page } from "./page";

const STATUS_LABEL: Record<Page["uploadStatus"], string> = {
  pending: "Queued",
  uploading: "Uploading",
  uploaded: "Uploaded",
  failed: "Retrying",
  blocked: "Blocked",
};

const BUCKET_LABEL: Record<Page["uploadStatus"], string> = {
  pending: "queued",
  uploading: "uploading",
  uploaded: "uploaded",
  failed: "retrying",
  blocked: "blocked",
};

const ORDER: Page["uploadStatus"][] = ["uploaded", "pending", "uploading", "failed", "blocked"];

export function pageStatusBreakdown(pages: Page[]): string {
  const counts = new Map<Page["uploadStatus"], number>();
  for (const p of pages) counts.set(p.uploadStatus, (counts.get(p.uploadStatus) ?? 0) + 1);
  return ORDER.filter(status => counts.get(status))
    .map(status => `${counts.get(status)} ${BUCKET_LABEL[status]}`)
    .join(" · ");
}

export function blockedPageNotice(pages: Page[]): string | null {
  const count = pages.filter(p => p.uploadStatus === "blocked").length;
  if (count === 0) return null;
  return `${count} page${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} attention — see Needs attention on Home.`;
}

/** What the scanner should actually do about a page that isn't
 * progressing. Only reasons with an action worth naming get text: a
 * transient network error on a "Retrying" page explains itself, and the
 * `String(e)` we store for it is not something to put in front of
 * scanning staff. */
const FAIL_REASON_LABEL: Record<string, string> = {
  "remote-mismatch": "Server already holds a different image for this page — retake it to send a new one.",
  "checksum-mismatch": "Upload arrived corrupted twice — retake this page.",
  // Retaking, not retrying: the photo never reached device storage, so the
  // queue has nothing to send however many times it's asked.
  "idb-write": "Couldn't save to this device — free up space, then retake this page.",
  "missing-blob": "This photo is no longer on the device — retake this page.",
  checksum: "Couldn't read this photo — retake it.",
  "missing-script": "Script record is missing — retry, or remove this page.",
};

/** The per-page word on the script screen. A failed page with no image
 * behind it says "Needs retake" rather than "Retrying": the queue skips
 * pages whose checksum never landed, and so does the manual trigger, so
 * "Retrying" was a status that would never change on its own. */
export function pageStatusLabel(page: Page): string {
  if (!isUploadReady(page)) return page.uploadStatus === "failed" ? "Needs retake" : "Saving…";
  return STATUS_LABEL[page.uploadStatus];
}

/** Blocked and failed only: every other status is either progressing or
 * done, and a stale `failReason` lingers on a page the queue has already
 * picked up again. */
export function failReasonLabel(page: Page): string | null {
  if (page.uploadStatus !== "blocked" && page.uploadStatus !== "failed") return null;
  if (!page.failReason) return null;
  return FAIL_REASON_LABEL[page.failReason] ?? null;
}

export interface ManualUploadPrompt {
  /** The rows to hand to `UploadQueue.retryNow` — the caller's list minus
   * anything the queue can't act on. */
  pages: Page[];
  count: number;
  message: string;
  action: string;
}

/** Copy for the manual upload trigger, shared by Home (per script) and the
 * script screen so both read the same. Null means there is nothing to send
 * and the button should not be on screen at all. The wording follows the
 * work: pages the queue is already carrying are waiting to *upload*, pages
 * it has given up on for now are waiting to *retry*. */
export function manualUploadPrompt(pages: Page[]): ManualUploadPrompt | null {
  const ids = new Set(manualUploadPageIds(pages));
  if (ids.size === 0) return null;
  const targets = pages.filter(p => ids.has(p.id));
  const stalled = targets.some(p => p.uploadStatus === "failed" || p.uploadStatus === "blocked");
  return {
    pages: targets,
    count: targets.length,
    message: `${targets.length} ${targets.length === 1 ? "page" : "pages"} waiting to ${stalled ? "retry" : "upload"}`,
    action: stalled ? "Retry now" : "Upload now",
  };
}
